// src/Login.jsx — SAKSHAM Digital Field Operations System
// Design  : Original UI/UX layout preserved exactly
// Changes : Role dropdown → Email field (individual login)
//           + Audit Logger + Status check + Bilingual errors expanded

import { useState, useEffect } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "./firebase-core";
import LOGO from "./shaksham_logo_tamil.png";   // ← updated logo file name

import { logAudit, AUDIT_ACTION, AUDIT_MODULE } from "./utils/auditLogger";

export default function LoginPage({ onLogin }) {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  useEffect(() => {
    const cb = () => import("./pages/DashboardPage");
    if (typeof requestIdleCallback !== "undefined") {
      requestIdleCallback(cb);
    } else {
      setTimeout(cb, 2000);
    }
  }, []);

  async function handleLogin(e) {
    e.preventDefault();

    // ── Basic validation ──────────────────────────────────────
    if (!email.trim()) {
      setError(
        "மின்னஞ்சல் முகவரி உள்ளிடுங்கள்.\n" +
        "Please enter your email address."
      );
      return;
    }
    if (!password.trim()) {
      setError(
        "Password உள்ளிடுங்கள்.\n" +
        "Please enter your password."
      );
      return;
    }

    setError("");
    setLoading(true);

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();

    try {
      // Step 1 — Firebase Auth login
      const userCredential = await signInWithEmailAndPassword(
        auth, trimmedEmail, trimmedPassword
      );
      const uid = userCredential.user.uid;

      // Step 2 — Fetch user profile (lazy-load full Firebase so Firestore
      // SDK is not in the initial Login bundle)
      const [{ db }, { doc, getDoc, query, collection, where, getDocs, setDoc }] = await Promise.all([
        import("./firebase"),
        import("firebase/firestore"),
      ]);
      const profileRef = doc(db, "users", uid);
      const userDocSnap = await getDoc(profileRef);

      let userData;

      if (userDocSnap.exists()) {
        userData = userDocSnap.data();
      } else {
        // The Firestore profile's document id can drift away from the
        // current Firebase Auth uid — e.g. the Auth account was deleted
        // and re-created for the same email without anyone updating
        // Firestore to match. Rather than failing the login outright,
        // look the profile up by email; if one turns up, self-heal by
        // copying it to the correct uid so this (and anything else keyed
        // by uid, like password reset) works directly from now on.
        const staleSnap = await getDocs(
          query(collection(db, "users"), where("email", "==", trimmedEmail))
        );

        if (staleSnap.empty) {
          await auth.signOut();
          setError(
            "User profile கிடைக்கவில்லை. Admin-ஐ தொடர்பு கொள்ளுங்கள்.\n" +
            "User profile not found. Please contact your Admin."
          );
          setLoading(false);

          await logAudit({
            user:       null,
            action:     AUDIT_ACTION.LOGIN_FAILED,
            module:     AUDIT_MODULE.AUTH,
            targetId:   uid,
            targetName: trimmedEmail,
            notes:      "Firestore user profile not found after Auth success",
          });
          return;
        }

        const staleDoc = staleSnap.docs[0];
        userData = staleDoc.data();

        try {
          await setDoc(profileRef, { ...userData, uid });
          await logAudit({
            user:       null,
            action:     AUDIT_ACTION.EDIT,
            module:     AUDIT_MODULE.AUTH,
            targetId:   uid,
            targetName: userData.name || trimmedEmail,
            notes:      `Self-healed user profile: relinked from stale doc ${staleDoc.id} to current Auth uid ${uid}`,
          });
        } catch (healErr) {
          // Not fatal — log in with the stale doc's data even if the
          // self-heal write itself failed (e.g. a rules edge case).
          console.error("Profile self-heal failed:", healErr);
        }
      }

      // Step 3 — Check if account is active
      if (userData.status && userData.status !== "Active" && userData.status !== "active") {
        await auth.signOut();
        setError(
          "உங்கள் account inactive ஆக உள்ளது. Admin-ஐ தொடர்பு கொள்ளுங்கள்.\n" +
          "Your account is inactive. Please contact your Admin."
        );
        setLoading(false);

        await logAudit({
          user:       null,
          action:     AUDIT_ACTION.LOGIN_FAILED,
          module:     AUDIT_MODULE.AUTH,
          targetId:   uid,
          targetName: userData.name || trimmedEmail,
          notes:      `Login blocked — account status: ${userData.status}`,
        });
        return;
      }

      // Step 4 — Build full user object
      // Include all scope fields so every module can filter by role
      const loggedInUser = {
        uid,
        name:        userData.name        || "",
        email:       userData.email       || trimmedEmail,
        role:        userData.role        || "",
        district:    userData.district    || "",
        ouIds:       userData.ouIds       || [],
        stateIds:    userData.stateIds    || [],
        districtIds: userData.districtIds || [],
        projectIds:  userData.projectIds  || [],
        phone:       userData.phone       || "",
        status:      userData.status      || "Active",
      };

      // Step 5 — Audit: LOGIN SUCCESS
      await logAudit({
        user:       loggedInUser,
        action:     AUDIT_ACTION.LOGIN,
        module:     AUDIT_MODULE.AUTH,
        targetId:   uid,
        targetName: userData.name,
        notes:      `Logged in from ${userData.district || "Unknown district"}`,
      });

      // Step 6 — Pass full user object to App
      onLogin(loggedInUser);

    } catch (err) {
      console.error("Login error:", err.code);

      // Audit: LOGIN FAILED
      await logAudit({
        user:       null,
        action:     AUDIT_ACTION.LOGIN_FAILED,
        module:     AUDIT_MODULE.AUTH,
        targetId:   trimmedEmail,
        targetName: `Failed attempt — ${trimmedEmail}`,
        notes:      `Error code: ${err.code}`,
      });

      // Bilingual error messages — Tamil + English
      if (
        err.code === "auth/wrong-password"     ||
        err.code === "auth/invalid-credential" ||
        err.code === "auth/user-not-found"
      ) {
        setError(
          "மின்னஞ்சல் அல்லது Password தவறாக உள்ளது. மீண்டும் முயற்சிக்கவும்.\n" +
          "Incorrect email or password. Please try again."
        );
      } else if (err.code === "auth/invalid-email") {
        setError(
          "தவறான மின்னஞ்சல் முகவரி. சரியாக உள்ளிடுங்கள்.\n" +
          "Invalid email address. Please enter a valid email."
        );
      } else if (err.code === "auth/user-disabled") {
        setError(
          "இந்த கணக்கு முடக்கப்பட்டுள்ளது. Admin-ஐ தொடர்பு கொள்ளுங்கள்.\n" +
          "This account has been disabled. Contact your Admin."
        );
      } else if (err.code === "auth/too-many-requests") {
        setError(
          "பல முறை தவறான முயற்சி — சிறிது நேரம் காத்திருங்கள்.\n" +
          "Too many failed attempts. Please wait a few minutes."
        );
      } else if (err.code === "auth/network-request-failed") {
        setError(
          "Internet connection சரி பார்க்கவும்.\n" +
          "Network error. Please check your internet connection."
        );
      } else {
        setError(
          "Login தோல்வி. மீண்டும் முயற்சிக்கவும்.\n" +
          "Login failed. Please try again."
        );
      }
      setLoading(false);
    }
  }

  // ── Render — original design preserved exactly ──────────────
  return (
    <div style={{
      minHeight:   "100vh",
      display:     "flex",
      alignItems:  "center",
      justifyContent: "center",
      background:  "linear-gradient(135deg,#0a2e1f 0%,#1a6b4a 60%,#16a34a 100%)",
      padding:     16,
      fontFamily:  "Segoe UI, sans-serif",
    }}>

      {/* Dot-pattern background */}
      <div style={{
        position:        "fixed", inset: 0, opacity: 0.04, pointerEvents: "none",
        backgroundImage: "radial-gradient(circle,#fff 1px,transparent 1px)",
        backgroundSize:  "32px 32px",
      }} />

      {/* Card */}
      <div style={{
        background:   "#fff",
        borderRadius: 20,
        padding:      "36px 32px",
        width:        "100%",
        maxWidth:     420,
        boxShadow:    "0 24px 80px rgba(0,0,0,0.35)",
        position:     "relative",
      }}>

        {/* Logo + Header */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <img
            src={LOGO}
            alt="SAKSHAM — மாற்றுத்திறனாளர்கள் நலன் விரும்பும் தேசிய அமைப்பு"
            style={{ height: 72, objectFit: "contain", marginBottom: 10 }}
          />
          <div style={{ fontSize: 13, color: "#1e293b", letterSpacing: 1 }}>
            பதிவு எண் : TN – 16/2015
          </div>
          <div style={{
            height:     2,
            background: "linear-gradient(90deg,transparent,#1a6b4a,transparent)",
            margin:     "12px 0",
          }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>
            Sign in to SAKSHAM Portal
          </div>
          <div style={{ fontSize: 13, color: "#1e293b", marginTop: 4 }}>
            Authorised users only · All access is monitored
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} noValidate>

          {/* ── Email field (replaces Role dropdown) ── */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(""); }}
              onKeyDown={e => { if (e.key.length === 1 && e.key.charCodeAt(0) > 127) e.preventDefault(); }}
              placeholder="உங்கள் மின்னஞ்சல் / Your email"
              aria-label="Email address"
              style={inputStyle}
              autoComplete="email"
              onFocus={e => (e.target.style.borderColor = "#1a6b4a")}
              onBlur={e  => (e.target.style.borderColor = "#e2e8f0")}
            />
          </div>

          {/* ── Password ── */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(""); }}
                placeholder="Password உள்ளிடுங்கள்"
                aria-label="Password"
                style={{ ...inputStyle, paddingRight: 44 }}
                autoComplete="current-password"
                onFocus={e => (e.target.style.borderColor = "#1a6b4a")}
                onBlur={e  => (e.target.style.borderColor = "#e2e8f0")}
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                aria-label={showPwd ? "Hide password" : "Show password"}
                style={{
                  position:  "absolute", right: 12, top: "50%",
                  transform: "translateY(-50%)",
                  background:"none", border: "none",
                  cursor:    "pointer", fontSize: 16, color: "#1e293b",
                  minWidth: 44, minHeight: 44,
                }}
              >
                {showPwd ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          {/* ── Error Message — Bilingual Tamil + English ── */}
          {error && (
            <div role="alert" aria-live="assertive" style={{
              background:   "#fef2f2",
              border:       "1px solid #fecaca",
              borderRadius: 8,
              padding:      "10px 12px",
              fontSize:     13,
              color:        "#dc2626",
              marginBottom: 12,
              lineHeight:   1.7,
              whiteSpace:   "pre-line",
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* ── Submit Button ── */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width:        "100%",
              background:   loading
                              ? "#475569"
                              : "linear-gradient(135deg,#0f3d2e,#1a6b4a)",
              color:        "#fff",
              border:       "none",
              borderRadius: 10,
              padding:      "12px",
              fontSize:     14,
              fontWeight:   700,
              cursor:       loading ? "not-allowed" : "pointer",
              letterSpacing:0.5,
              boxShadow:    loading ? "none" : "0 4px 16px rgba(26,107,74,0.35)",
            }}
          >
            {loading ? "Logging in…" : "🔐  Sign In to SAKSHAM"}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "#1e293b" }}>
          Forgot password? Contact your{" "}
          <strong style={{ color: "#1a6b4a" }}>Trust Admin</strong>
        </div>
      </div>
    </div>
  );
}

// ── Shared styles — original preserved ───────────────────────
const labelStyle = {
  fontSize:     14,
  fontWeight:   600,
  color:        "#374151",
  display:      "block",
  marginBottom: 5,
};

const inputStyle = {
  width:        "100%",
  border:       "1.5px solid #475569",
  borderRadius: 10,
  padding:      "10px 14px",
  fontSize:     16,
  color:        "#1e293b",
  background:   "#f8fafc",
  outline:      "none",
  cursor:       "text",
  boxSizing:    "border-box",
  fontFamily:   "Segoe UI, sans-serif",
};
